import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import Reveal from '../../components/Reveal/Reveal';
import ArrowIcon from '../../components/icons/ArrowIcon';
import TerminalBlock from '../../components/TerminalBlock/TerminalBlock';
import { getOrganizationSchema } from '../../utils/structuredData';
import {
  IconMobile, IconWeb, IconEnterprise, IconCloud, IconDesign, IconApi,
  IconShield, IconRetail, IconHealth, IconLogistics, IconFinance,
  IconRealEstate, IconEducation, IconUsers, IconTarget, IconScale,
} from '../../components/icons/ServiceIcons';

const PUBLIC_URL = process.env.PUBLIC_URL || '';
const HERO_BG = { '--hero-bg': `url(${PUBLIC_URL}/assets/hero-banner.png)` };
const CTA_BG = { '--cta-bg': `url(${PUBLIC_URL}/assets/cta-banner.png)` };

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const SERVICES = [
  {
    num: '01',
    icon: <IconEnterprise size={28} />,
    title: 'Custom Software Development',
    desc: 'Business applications built around your workflows — CRM, ERP-style tools, internal portals, and process automation.',
  },
  {
    num: '02',
    icon: <IconWeb size={28} />,
    title: 'Web Application Development',
    desc: 'Secure, responsive web apps and customer portals using modern frameworks, designed to stay maintainable as you grow.',
  },
  {
    num: '03',
    icon: <IconMobile size={28} />,
    title: 'Mobile App Development',
    desc: 'iOS and Android apps for customers or field teams — from MVP to production, including store submission support.',
  },
  {
    num: '04',
    icon: <IconCloud size={28} />,
    title: 'Cloud & DevOps',
    desc: 'Cloud setup, CI/CD pipelines, deployments, monitoring, and practical support so releases stay predictable.',
  },
  {
    num: '05',
    icon: <IconDesign size={28} />,
    title: 'UI / UX Design',
    desc: 'Clean interfaces and prototypes that make your product easy to use — before and during development.',
  },
  {
    num: '06',
    icon: <IconApi size={28} />,
    title: 'API Integration & Automation',
    desc: 'Connect payment gateways, CRMs, ERPs, and third-party tools so your systems work together instead of in silos.',
  },
];

const DIFFERENTIATORS = [
  {
    icon: <IconUsers />,
    title: 'Hands-on engineering',
    text: 'You work with people who understand the product, not a long chain of hand-offs.',
  },
  {
    icon: <IconTarget />,
    title: 'Clear scope and updates',
    text: 'We agree what will be built, then share progress regularly so there are fewer surprises.',
  },
  {
    icon: <IconShield />,
    title: 'Secure by default',
    text: 'Access control, safe coding practices, and sensible data handling from the start.',
  },
  {
    icon: <IconScale />,
    title: 'Built to maintain',
    text: 'Readable code, documentation, and a structure you can extend after launch.',
  },
];

const INDUSTRIES = [
  { icon: <IconRetail />, title: 'Retail & E-Commerce', text: 'Catalogs, orders, inventory, and customer-facing storefronts.' },
  { icon: <IconFinance />, title: 'Finance & Operations', text: 'Dashboards, workflows, and tools that support day-to-day business operations.' },
  { icon: <IconLogistics />, title: 'Logistics & Field Work', text: 'Tracking, dispatch-style apps, and mobile tools for on-ground teams.' },
  { icon: <IconHealth />, title: 'Healthcare', text: 'Scheduling, records-oriented apps, and internal clinic workflows.' },
  { icon: <IconRealEstate />, title: 'Real Estate', text: 'Listings, lead management, and property-related portals.' },
  { icon: <IconEducation />, title: 'Education', text: 'Learning portals, admissions flows, and admin tools for institutes.' },
];

const STACK = [
  'C# / .NET 8', 'ASP.NET Core', 'React', 'TypeScript', 'Node.js', 'Microsoft Azure',
  'AWS', 'SQL Server', 'PostgreSQL', 'Redis', 'Docker',
  'MAUI Blazor', 'React Native', 'REST APIs', 'Power BI',
];

const PROCESS = [
  { num: '01', title: 'Discover', text: 'We understand your goals, users, and constraints, then define a practical scope and timeline.' },
  { num: '02', title: 'Design', text: 'Wireframes and UI direction first, so everyone agrees on the product before heavy development.' },
  { num: '03', title: 'Build', text: 'Iterative development with regular demos, testing, and room for feedback along the way.' },
  { num: '04', title: 'Launch & Support', text: 'Go-live support, handover, and optional maintenance so the product stays healthy after release.' },
];

const ENGAGEMENT = [
  {
    icon: <IconTarget />,
    title: 'Fixed-scope project',
    desc: 'Best when requirements are clear. We agree deliverables, timeline, and cost upfront.',
    points: ['Defined milestones', 'Predictable budget', 'Handover included'],
    featured: true,
    badge: 'Common starting point',
  },
  {
    icon: <IconUsers />,
    title: 'Dedicated team',
    desc: 'A focused team for ongoing product work when your roadmap keeps evolving.',
    points: ['Monthly engagement', 'Direct communication', 'Flexible priorities'],
    featured: false,
  },
  {
    icon: <IconScale />,
    title: 'Team extension',
    desc: 'Add developers to your existing team for a defined period or skill gap.',
    points: ['Fast onboarding', 'Works in your process', 'Flexible duration'],
    featured: false,
  },
];

const MARQUEE_ITEMS = [
  'Custom Software', 'Web Applications', 'Mobile Apps', 'Cloud & DevOps',
  'API Integration', 'UI / UX Design', 'Product Support', 'Business Automation',
];

const OFFER_POINTS = [
  { title: 'Custom software', text: 'Internal tools and business platforms' },
  { title: 'Web & mobile', text: 'Customer apps and portals' },
  { title: 'Cloud & support', text: 'Deployments and ongoing help' },
  { title: 'Clear delivery', text: 'Scope, demos, and communication' },
];

const Home = () => {
  const location = useLocation();
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://www.codeinventivsolutions.com';

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [location]);

  return (
    <div className="home-page">
      <SEO
        title="CodeInventiv Solutions — Custom Software, Web & Mobile App Development"
        description="CodeInventiv Solutions builds custom software, web applications, and mobile apps for businesses. Founded in 2025. Based in Jaipur, India."
        keywords="software development company Jaipur, custom software development, web application development, mobile app development, IT services India, CodeInventiv Solutions"
        structuredData={getOrganizationSchema(siteUrl)}
        ogImage="/assets/logo-light.png"
      />

      <section className="hero-x" style={HERO_BG}>
        <div className="container hero-x-inner">
          <div className="hero-x-copy">
            <div className="hero-x-eyebrow">
              <span className="pulse" />
              Software Development Company
            </div>
            <Reveal>
              <h1 className="hero-x-title">
                Custom software, web &amp; mobile apps<br />
                for <em>growing businesses</em>
              </h1>
            </Reveal>
            <Reveal>
              <p className="hero-x-sub">
                CodeInventiv Solutions helps you plan, build, and support business software — from
                internal tools and customer portals to mobile apps and cloud setup. Clear scope,
                regular updates, and practical delivery.
              </p>
            </Reveal>
            <Reveal className="hero-x-points">
              <span className="hero-x-point"><CheckIcon /> Built by an experienced engineering team</span>
              <span className="hero-x-point"><CheckIcon /> Clear timelines and communication</span>
              <span className="hero-x-point"><CheckIcon /> Support after launch</span>
            </Reveal>
            <Reveal className="hero-x-actions">
              <Link to="/contact" className="btn btn-gold">
                Get a Quote
                <ArrowIcon />
              </Link>
              <Link to="/services" className="btn btn-ghost-light">View Services</Link>
            </Reveal>
          </div>

          <div className="hero-x-panel">
            <div className="hero-x-panel-title">What we offer</div>
            <ul className="hero-offer-list">
              {OFFER_POINTS.map((item) => (
                <li key={item.title} className="hero-offer-item">
                  <strong>{item.title}</strong>
                  <span>{item.text}</span>
                </li>
              ))}
            </ul>
            <div className="hero-x-tags">
              <span className="badge gold">.NET</span>
              <span className="badge gold">React</span>
              <span className="badge gold">Azure</span>
              <span className="badge">SQL Server</span>
              <span className="badge">Mobile</span>
            </div>
          </div>
        </div>
      </section>

      <div className="marquee-wrap brand" aria-hidden="true">
        <div className="marquee-track">
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
            <React.Fragment key={`${item}-${i}`}>
              <span className="marquee-item">{item}</span>
              <span className="marquee-sep" />
            </React.Fragment>
          ))}
        </div>
      </div>

      <section className="section section-light" id="services">
        <div className="container">
          <Reveal className="section-head">
            <div className="section-eyebrow">Our Services</div>
            <h2 className="section-title">Software services that support <em>real business needs</em></h2>
            <p className="section-sub">
              Whether you need a new product, a replacement for spreadsheets, or help extending an
              existing system — we focus on practical outcomes.
            </p>
          </Reveal>
          <div className="services-grid">
            {SERVICES.map((s) => (
              <Reveal key={s.num} as="article" className="svc-card">
                <div className="svc-top">
                  <div className="svc-icon">{s.icon}</div>
                  <span className="svc-num">{s.num}</span>
                </div>
                <h3 className="svc-title">{s.title}</h3>
                <p className="svc-desc">{s.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-pale" id="expertise">
        <div className="container about-x">
          <Reveal className="about-x-media">
            <img src="/assets/team-banner.png" alt="Software development work at CodeInventiv Solutions" loading="lazy" />
            <div className="about-x-chip">
              <span className="about-x-chip-num">2025</span>
              <span className="about-x-chip-lbl">Founded<br />in Jaipur</span>
            </div>
          </Reveal>
          <Reveal className="about-x-copy">
            <div className="section-eyebrow">About Us</div>
            <h2 className="section-title">A focused software partner for <em>businesses that want clarity</em></h2>
            <p className="section-sub">
              CodeInventiv Solutions was founded in 2025 to help companies turn ideas into working
              software without unnecessary complexity. We keep communication direct, scope realistic,
              and delivery easy to follow.
            </p>
            <div className="about-x-list">
              {DIFFERENTIATORS.map((item) => (
                <div key={item.title} className="about-x-item">
                  <div className="about-x-item-icon">{item.icon}</div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="about-x-actions">
              <Link to="/contact" className="btn btn-gold">
                Discuss Your Requirement
                <ArrowIcon />
              </Link>
              <Link to="/about" className="btn btn-line">About Us</Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section-dark industries-band" id="industries">
        <div className="container">
          <Reveal className="section-head">
            <div className="section-eyebrow on-dark">Industries</div>
            <h2 className="section-title on-dark">Software for teams across <em>different industries</em></h2>
            <p className="dark-lede" style={{ marginTop: '1rem' }}>
              We build tools for operations, customers, and internal teams. Here are common industries
              we work with.
            </p>
          </Reveal>
          <div className="industries-grid">
            {INDUSTRIES.map((item) => (
              <Reveal key={item.title} as="article" className="industry-card">
                <div className="industry-icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </Reveal>
            ))}
          </div>
          <Reveal className="industries-home-cta">
            <Link to="/industries" className="btn btn-ghost-light">
              Explore Industries
              <ArrowIcon />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="section section-light" id="stack">
        <div className="container">
          <Reveal className="section-head">
            <div className="section-eyebrow">Technology</div>
            <h2 className="section-title">Technologies we use to <em>build and ship</em></h2>
            <p className="section-sub">
              We choose proven tools that are reliable to develop with and easier to maintain over time.
            </p>
          </Reveal>
          <Reveal className="stack-grid">
            {STACK.map((tech) => (
              <div key={tech} className="stack-pill">
                <span className="pill-gem" />
                {tech}
              </div>
            ))}
          </Reveal>
          <Reveal className="stack-home-cta">
            <Link to="/technologies" className="btn btn-line">
              View Full Stack
              <ArrowIcon />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="section section-pale" id="process">
        <div className="container">
          <Reveal className="section-head">
            <div className="section-eyebrow">How We Work</div>
            <h2 className="section-title">A simple process from <em>idea to launch</em></h2>
            <p className="section-sub">
              Four stages keep the project organized — so you always know what is happening next.
            </p>
          </Reveal>
          <ol className="process-grid">
            {PROCESS.map((step) => (
              <Reveal key={step.num} as="li" className="process-step">
                <div className="process-num">{step.num}</div>
                <h3 className="process-title">{step.title}</h3>
                <p className="process-text">{step.text}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="section section-light" id="work">
        <div className="container">
          <Reveal className="section-head">
            <div className="section-eyebrow">Our Work</div>
            <h2 className="section-title">Selected projects <em>coming soon</em></h2>
            <p className="section-sub">
              We are preparing case studies with real project details and visuals. In the meantime,
              tell us about your requirement and we can share relevant examples on a call.
            </p>
          </Reveal>
          <Reveal className="work-placeholder">
            <p>
              Project thumbnails and write-ups will be added here shortly.
            </p>
            <Link to="/contact" className="btn btn-gold">
              Talk About Your Project
              <ArrowIcon />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="section-dark engineering-band">
        <div className="container engineering-grid">
          <Reveal>
            <div className="section-eyebrow on-dark">Delivery Approach</div>
            <h2 className="section-title on-dark">
              Clean code.<br />
              <em>Clear ownership.</em>
            </h2>
            <p className="dark-lede">
              We use version control, reviews, and staged environments so releases are controlled.
              You receive the source code and documentation for what we build.
            </p>
            <div className="eng-metrics">
              <div className="eng-metric">
                <span className="eng-metric-num">Source</span>
                <span className="eng-metric-lbl">Code handover</span>
              </div>
              <div className="eng-metric">
                <span className="eng-metric-num">Demos</span>
                <span className="eng-metric-lbl">Regular progress reviews</span>
              </div>
              <div className="eng-metric">
                <span className="eng-metric-num">Support</span>
                <span className="eng-metric-lbl">After launch, if needed</span>
              </div>
            </div>
          </Reveal>
          <TerminalBlock />
        </div>
      </section>

      <section className="section section-pale" id="engagement">
        <div className="container">
          <Reveal className="section-head">
            <div className="section-eyebrow">Engagement Models</div>
            <h2 className="section-title">Ways to <em>work with us</em></h2>
            <p className="section-sub">
              Pick the model that fits your stage. We can start small and adjust as the product grows.
            </p>
          </Reveal>
          <div className="engage-grid">
            {ENGAGEMENT.map((model) => (
              <Reveal
                key={model.title}
                as="article"
                className={`engage-card${model.featured ? ' featured' : ''}`}
              >
                {model.badge && <span className="engage-badge">{model.badge}</span>}
                <div className="engage-icon">{model.icon}</div>
                <h3>{model.title}</h3>
                <p>{model.desc}</p>
                <ul>
                  {model.points.map((p) => (
                    <li key={p}><CheckIcon />{p}</li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="cta-band" style={CTA_BG}>
        <div className="container cta-inner">
          <Reveal className="cta-copy">
            <h2>Have a project in mind? <em>Let&apos;s discuss it.</em></h2>
            <p>
              Share a short brief and we will come back with questions, a suggested approach, and
              next steps — at no cost.
            </p>
          </Reveal>
          <Reveal className="cta-actions">
            <Link to="/contact" className="btn btn-gold">
              Request a Quote
              <ArrowIcon />
            </Link>
            <Link to="/services" className="btn btn-ghost-light">Explore Services</Link>
          </Reveal>
        </div>
      </section>

      <section className="section section-light" id="contact">
        <div className="container">
          <Reveal className="contact-home-teaser">
            <div>
              <div className="section-eyebrow">Contact</div>
              <h2 className="section-title" style={{ marginBottom: 0 }}>
                Ready to talk about <em>your project?</em>
              </h2>
              <p>
                Reach us by form, email, phone, or WhatsApp. We typically respond within 24 hours.
                NDA available before detailed discussion.
              </p>
            </div>
            <div className="contact-home-teaser-actions">
              <Link to="/contact" className="btn btn-gold">
                Go to Contact
                <ArrowIcon />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
